import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image as PdfImage,
} from "@react-pdf/renderer"
import {
  itemUnits,
  statuses,
  calculateLineNet,
  type InvoiceData,
  type ColorScheme,
  type LineItem,
} from "./invoice-generator-constants"

interface InvoicePdfDocumentProps {
  invoice: InvoiceData
  colors: ColorScheme
  formatMoney: (amount: number) => string
  subtotal: number
  taxAmount: number
  discountAmount: number
  shippingAmount: number
  total: number
  balanceDue: number
  docTitle: string
}

function quantityLabel(item: LineItem): string {
  if (item.unit === "fixed") return "—"
  const unitMeta = itemUnits.find((u) => u.id === (item.unit || "units"))
  const suffix = unitMeta?.suffix || ""
  return `${item.quantity}${suffix}`
}

export function InvoicePdfDocument({
  invoice,
  colors,
  formatMoney,
  subtotal,
  taxAmount,
  discountAmount,
  shippingAmount,
  total,
  balanceDue,
  docTitle,
}: InvoicePdfDocumentProps) {
  const styles = StyleSheet.create({
    page: { padding: 40, fontSize: 10, fontFamily: "Helvetica", color: "#1f2937", position: "relative" },
    header: { flexDirection: "row", justifyContent: "space-between", marginBottom: 30 },
    logo: { maxWidth: 120, maxHeight: 50 },
    title: { fontSize: 28, fontWeight: "bold", color: colors.accent, marginBottom: 8 },
    statusBadge: {
      alignSelf: "flex-end",
      paddingHorizontal: 8,
      paddingVertical: 2,
      borderRadius: 999,
      fontSize: 8,
      fontWeight: "bold",
      textTransform: "uppercase",
      letterSpacing: 0.5,
      marginBottom: 6,
    },
    metaRow: { flexDirection: "row", justifyContent: "flex-end", marginBottom: 3 },
    metaLabel: { color: "#6b7280", marginRight: 8, width: 50, textAlign: "right" },
    metaValue: { width: 90, textAlign: "right" },
    parties: { flexDirection: "row", marginBottom: 25, gap: 30 },
    partyCol: { flex: 1 },
    partyLabel: { fontSize: 8, fontWeight: "bold", color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 },
    partyText: { fontSize: 10, lineHeight: 1.5 },
    partyTaxId: { fontSize: 8, color: "#6b7280", marginTop: 4 },
    itemDetails: { fontSize: 8, color: "#6b7280", marginTop: 2, lineHeight: 1.4 },
    table: { marginBottom: 20 },
    tableHeader: { flexDirection: "row", backgroundColor: colors.tableBg, padding: 10 },
    tableHeaderText: { fontSize: 8, fontWeight: "bold", color: colors.tableText, textTransform: "uppercase", letterSpacing: 0.5 },
    tableRow: { flexDirection: "row", borderBottomWidth: 1, borderBottomColor: "#e5e7eb", padding: 10 },
    colDesc: { flex: 3 },
    colQty: { width: 50, textAlign: "right" },
    colRate: { width: 70, textAlign: "right" },
    colAmount: { width: 80, textAlign: "right" },
    lineAdjustment: { fontSize: 8, color: "#9ca3af", marginTop: 2 },
    totalsSection: { flexDirection: "row", justifyContent: "space-between", marginBottom: 20 },
    notesBox: { flex: 1, maxWidth: 250 },
    notesLabel: { fontSize: 8, fontWeight: "bold", color: "#6b7280", textTransform: "uppercase", marginBottom: 4 },
    notesText: { fontSize: 9, color: "#4b5563", lineHeight: 1.4 },
    totalsBox: { width: 220 },
    totalRow: { flexDirection: "row", justifyContent: "space-between", paddingVertical: 4 },
    totalLabel: { color: "#4b5563" },
    totalValue: { fontWeight: "bold" },
    balanceDue: { flexDirection: "row", justifyContent: "space-between", alignItems: "center", backgroundColor: `${colors.accent}15`, borderLeftWidth: 3, borderLeftColor: colors.accent, padding: 10, marginTop: 8, borderRadius: 4 },
    balanceLabel: { fontWeight: "bold", color: colors.accent },
    balanceValue: { fontSize: 14, fontWeight: "bold", color: colors.accent },
    footer: { borderTopWidth: 1, borderTopColor: "#e5e7eb", paddingTop: 15, marginTop: 10, flexDirection: "row", gap: 30 },
    footerCol: { flex: 1 },
    footerLabel: { fontSize: 8, fontWeight: "bold", color: "#6b7280", textTransform: "uppercase", marginBottom: 4 },
    footerText: { fontSize: 9, color: "#4b5563", lineHeight: 1.4 },
    sidebarContainer: { flexDirection: "row", margin: -40 },
    sidebar: { width: 160, backgroundColor: colors.accent, padding: 25, color: "white" },
    sidebarTitle: { fontSize: 20, fontWeight: "bold", color: "white", marginBottom: 12 },
    sidebarStatus: {
      alignSelf: "flex-start",
      paddingHorizontal: 6,
      paddingVertical: 2,
      borderRadius: 999,
      fontSize: 7,
      fontWeight: "bold",
      textTransform: "uppercase",
      backgroundColor: "rgba(255,255,255,0.18)",
      color: "white",
      marginBottom: 16,
    },
    sidebarLabel: { fontSize: 8, color: "rgba(255,255,255,0.6)", textTransform: "uppercase", marginBottom: 2 },
    sidebarValue: { fontSize: 10, color: "white", marginBottom: 12 },
    sidebarBalance: { marginTop: 25, paddingTop: 20, borderTopWidth: 1, borderTopColor: "rgba(255,255,255,0.2)" },
    sidebarBalanceValue: { fontSize: 18, fontWeight: "bold", color: "white", marginTop: 4 },
    mainContent: { flex: 1, padding: 30 },
    centered: { textAlign: "center", alignItems: "center" },
    modernParties: { flexDirection: "row", gap: 40, marginBottom: 25 },
    modernPartyBox: { flex: 1, padding: 20, borderWidth: 1, borderColor: "#e5e7eb", borderRadius: 8, alignItems: "center" },
    compactHeader: { flexDirection: "row", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 },
    compactBalanceBox: { backgroundColor: `${colors.accent}15`, borderLeftWidth: 4, borderLeftColor: colors.accent, padding: 12, borderRadius: 4 },
    compactBalanceLabel: { fontSize: 8, color: "#6b7280", textTransform: "uppercase" },
    compactBalanceValue: { fontSize: 20, fontWeight: "bold", color: colors.accent },
    splitHeader: { flexDirection: "row", justifyContent: "space-between", alignItems: "center", borderBottomWidth: 2, borderBottomColor: colors.accent, paddingBottom: 20, marginBottom: 20 },
    splitMeta: { flexDirection: "row", justifyContent: "space-between", marginBottom: 25 },
    splitMetaLeft: { flex: 1 },
    splitMetaRow: { flexDirection: "row", marginBottom: 3 },
    splitMetaLabel: { color: "#9ca3af", width: 60 },
    splitPartyBox: { flex: 1, backgroundColor: "#f9fafb", padding: 15, borderRadius: 6 },
    watermark: {
      position: "absolute",
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      pointerEvents: "none",
    },
    watermarkText: {
      fontSize: 140,
      fontWeight: "bold",
      transform: "rotate(-25deg)",
      opacity: 0.07,
      letterSpacing: 8,
    },
  })

  const statusConfig = statuses.find((s) => s.id === invoice.status)
  const showWatermark =
    invoice.status === "paid" || invoice.status === "draft" || invoice.status === "overdue"
  const watermarkColor =
    invoice.status === "paid" ? "#047857" : invoice.status === "overdue" ? "#b91c1c" : "#6b7280"

  const validItems = invoice.items.filter((item) => item.description || item.rate > 0)

  const renderStatusBadge = () => {
    if (!statusConfig || invoice.status === "none") return null
    return (
      <Text style={[styles.statusBadge, { backgroundColor: statusConfig.bg, color: statusConfig.color }]}>
        {statusConfig.label}
      </Text>
    )
  }

  const renderWatermark = () =>
    showWatermark && (
      <View style={styles.watermark} fixed>
        <Text style={[styles.watermarkText, { color: watermarkColor }]}>
          {invoice.status.toUpperCase()}
        </Text>
      </View>
    )

  const renderTaxIdLine = (taxId: string | undefined, align: "left" | "center" = "left") =>
    taxId ? (
      <Text
        style={
          align === "center"
            ? [styles.partyTaxId, { textAlign: "center" as const }]
            : styles.partyTaxId
        }
      >
        Tax ID: {taxId}
      </Text>
    ) : null

  const renderTable = () => (
    <View style={styles.table}>
      <View style={styles.tableHeader}>
        <Text style={[styles.tableHeaderText, styles.colDesc]}>Description</Text>
        <Text style={[styles.tableHeaderText, styles.colQty]}>Qty</Text>
        <Text style={[styles.tableHeaderText, styles.colRate]}>Rate</Text>
        <Text style={[styles.tableHeaderText, styles.colAmount]}>Amount</Text>
      </View>
      {validItems.map((item, i) => {
        const lineNet = calculateLineNet(item)
        const gross = item.quantity * item.rate
        const hasAdjustment = !!(item.discount || item.taxRate)
        return (
          <View key={i} style={styles.tableRow}>
            <View style={styles.colDesc}>
              <Text>{item.description || ""}</Text>
              {item.details ? <Text style={styles.itemDetails}>{item.details}</Text> : null}
              {hasAdjustment && (
                <Text style={styles.lineAdjustment}>
                  {item.discount
                    ? `Discount ${item.discount}${item.discountType === "fixed" ? "" : "%"}`
                    : ""}
                  {item.discount && item.taxRate ? " · " : ""}
                  {item.taxRate ? `Tax ${item.taxRate}%` : ""}
                </Text>
              )}
            </View>
            <Text style={styles.colQty}>{quantityLabel(item)}</Text>
            <Text style={styles.colRate}>{formatMoney(item.rate)}</Text>
            <View style={styles.colAmount}>
              <Text style={{ fontWeight: "bold" }}>{formatMoney(lineNet)}</Text>
              {hasAdjustment && gross !== lineNet && (
                <Text style={[styles.lineAdjustment, { textDecoration: "line-through" }]}>
                  {formatMoney(gross)}
                </Text>
              )}
            </View>
          </View>
        )
      })}
    </View>
  )

  const renderTotals = (showNotes = true) => (
    <View style={styles.totalsSection}>
      {showNotes && invoice.notes ? (
        <View style={styles.notesBox}>
          <Text style={styles.notesLabel}>Notes</Text>
          <Text style={styles.notesText}>{invoice.notes}</Text>
        </View>
      ) : (
        <View />
      )}
      <View style={styles.totalsBox}>
        <View style={styles.totalRow}>
          <Text style={styles.totalLabel}>Subtotal</Text>
          <Text style={styles.totalValue}>{formatMoney(subtotal)}</Text>
        </View>
        {invoice.showDiscount && discountAmount > 0 && (
          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>
              Discount{" "}
              {invoice.discountType === "fixed"
                ? `(${formatMoney(invoice.discountRate)})`
                : `(${invoice.discountRate}%)`}
            </Text>
            <Text style={{ color: "#dc2626" }}>-{formatMoney(discountAmount)}</Text>
          </View>
        )}
        {invoice.taxes.length > 0 ? (
          invoice.taxes.map((t) => (
            <View key={t.id} style={styles.totalRow}>
              <Text style={styles.totalLabel}>
                {t.name} ({t.rate}%){t.compound ? " · compound" : ""}
              </Text>
              <Text>
                {formatMoney(
                  taxAmount && invoice.taxes.reduce((s, x) => s + x.rate, 0)
                    ? (taxAmount * t.rate) / invoice.taxes.reduce((s, x) => s + x.rate, 0)
                    : 0
                )}
              </Text>
            </View>
          ))
        ) : invoice.taxRate > 0 ? (
          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>
              {invoice.taxName || "Tax"} ({invoice.taxRate}%)
            </Text>
            <Text>{formatMoney(taxAmount)}</Text>
          </View>
        ) : null}
        {invoice.showShipping && shippingAmount > 0 && (
          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>Shipping</Text>
            <Text>{formatMoney(shippingAmount)}</Text>
          </View>
        )}
        {invoice.taxInclusive && (
          <View style={styles.totalRow}>
            <Text style={[styles.totalLabel, { fontSize: 8, fontStyle: "italic" }]}>
              Prices include tax
            </Text>
            <Text> </Text>
          </View>
        )}
        <View style={[styles.totalRow, { borderTopWidth: 1, borderTopColor: "#e5e7eb", marginTop: 4, paddingTop: 6 }]}>
          <Text style={styles.totalLabel}>Total</Text>
          <Text style={styles.totalValue}>{formatMoney(total)}</Text>
        </View>
        {invoice.amountPaid > 0 && (
          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>Amount Paid</Text>
            <Text>-{formatMoney(invoice.amountPaid)}</Text>
          </View>
        )}
        <View style={styles.balanceDue}>
          <Text style={styles.balanceLabel}>
            {invoice.documentType === "quote"
              ? "Quote Total"
              : invoice.documentType === "receipt"
              ? "Total Paid"
              : "Balance Due"}
          </Text>
          <Text style={styles.balanceValue}>
            {formatMoney(invoice.documentType === "receipt" ? total : balanceDue)}
          </Text>
        </View>
      </View>
    </View>
  )

  const renderFooter = () => {
    if (!invoice.terms && !invoice.paymentInfo) return null
    return (
      <View style={styles.footer}>
        {invoice.terms && (
          <View style={styles.footerCol}>
            <Text style={styles.footerLabel}>Terms & Conditions</Text>
            <Text style={styles.footerText}>{invoice.terms}</Text>
          </View>
        )}
        {invoice.paymentInfo && (
          <View style={styles.footerCol}>
            <Text style={styles.footerLabel}>Payment Information</Text>
            <Text style={styles.footerText}>{invoice.paymentInfo}</Text>
          </View>
        )}
      </View>
    )
  }

  if (invoice.layout === "sidebar") {
    return (
      <Document>
        <Page size="A4" style={[styles.page, { padding: 0 }]}>
          {renderWatermark()}
          <View style={styles.sidebarContainer}>
            <View style={styles.sidebar}>
              {invoice.logo && <PdfImage src={invoice.logo} style={[styles.logo, { marginBottom: 20 }]} />}
              <Text style={styles.sidebarTitle}>{docTitle}</Text>
              {invoice.status !== "none" && statusConfig && (
                <Text style={styles.sidebarStatus}>{statusConfig.label}</Text>
              )}
              <Text style={styles.sidebarLabel}>Number</Text>
              <Text style={styles.sidebarValue}>{invoice.invoiceNumber}</Text>
              <Text style={styles.sidebarLabel}>Date</Text>
              <Text style={styles.sidebarValue}>{invoice.issueDate}</Text>
              <Text style={styles.sidebarLabel}>Due</Text>
              <Text style={styles.sidebarValue}>{invoice.dueDate}</Text>
              <Text style={styles.sidebarLabel}>Terms</Text>
              <Text style={styles.sidebarValue}>{invoice.paymentTerms}</Text>
              {invoice.poNumber ? (
                <>
                  <Text style={styles.sidebarLabel}>PO #</Text>
                  <Text style={styles.sidebarValue}>{invoice.poNumber}</Text>
                </>
              ) : null}
              <View style={styles.sidebarBalance}>
                <Text style={styles.sidebarLabel}>
                  {invoice.documentType === "receipt" ? "Total Paid" : "Balance Due"}
                </Text>
                <Text style={styles.sidebarBalanceValue}>
                  {formatMoney(invoice.documentType === "receipt" ? total : balanceDue)}
                </Text>
              </View>
            </View>
            <View style={styles.mainContent}>
              <View style={styles.parties}>
                <View style={styles.partyCol}>
                  <Text style={styles.partyLabel}>From</Text>
                  <Text style={styles.partyText}>{invoice.from}</Text>
                  {renderTaxIdLine(invoice.fromTaxId)}
                </View>
                <View style={styles.partyCol}>
                  <Text style={styles.partyLabel}>Bill To</Text>
                  <Text style={styles.partyText}>{invoice.billTo}</Text>
                  {renderTaxIdLine(invoice.billToTaxId)}
                </View>
              </View>
              {renderTable()}
              {renderTotals()}
              {renderFooter()}
            </View>
          </View>
        </Page>
      </Document>
    )
  }

  if (invoice.layout === "compact") {
    return (
      <Document>
        <Page size="A4" style={styles.page}>
          {renderWatermark()}
          <View style={styles.compactHeader}>
            <View style={{ flexDirection: "row", alignItems: "center", gap: 15 }}>
              {invoice.logo && <PdfImage src={invoice.logo} style={styles.logo} />}
              <View>
                <Text style={[styles.title, { fontSize: 22, marginBottom: 2 }]}>{docTitle}</Text>
                <Text style={{ color: "#6b7280" }}>{invoice.invoiceNumber}</Text>
                {renderStatusBadge()}
              </View>
            </View>
            <View style={styles.compactBalanceBox}>
              <Text style={styles.compactBalanceLabel}>
                {invoice.documentType === "receipt" ? "Amount Paid" : "Balance Due"}
              </Text>
              <Text style={styles.compactBalanceValue}>
                {formatMoney(invoice.documentType === "receipt" ? total : balanceDue)}
              </Text>
            </View>
          </View>
          <View style={[styles.parties, { gap: 15 }]}>
            <View style={styles.partyCol}>
              <Text style={styles.partyLabel}>From</Text>
              <Text style={styles.partyText}>{invoice.from}</Text>
              {renderTaxIdLine(invoice.fromTaxId)}
            </View>
            <View style={styles.partyCol}>
              <Text style={styles.partyLabel}>Bill To</Text>
              <Text style={styles.partyText}>{invoice.billTo}</Text>
              {renderTaxIdLine(invoice.billToTaxId)}
            </View>
            <View style={styles.partyCol}>
              <View style={styles.splitMetaRow}><Text style={styles.splitMetaLabel}>Date:</Text><Text>{invoice.issueDate}</Text></View>
              <View style={styles.splitMetaRow}><Text style={styles.splitMetaLabel}>Due:</Text><Text>{invoice.dueDate}</Text></View>
              <View style={styles.splitMetaRow}><Text style={styles.splitMetaLabel}>Terms:</Text><Text>{invoice.paymentTerms}</Text></View>
              {invoice.poNumber ? (
                <View style={styles.splitMetaRow}><Text style={styles.splitMetaLabel}>PO #:</Text><Text>{invoice.poNumber}</Text></View>
              ) : null}
            </View>
          </View>
          {renderTable()}
          {renderTotals(false)}
          {renderFooter()}
        </Page>
      </Document>
    )
  }

  if (invoice.layout === "modern") {
    return (
      <Document>
        <Page size="A4" style={styles.page}>
          {renderWatermark()}
          <View style={[styles.centered, { marginBottom: 25 }]}>
            {invoice.logo && <PdfImage src={invoice.logo} style={[styles.logo, { marginBottom: 15 }]} />}
            <Text style={[styles.title, { fontSize: 32 }]}>{docTitle}</Text>
            {invoice.status !== "none" && statusConfig && (
              <Text style={[styles.statusBadge, { backgroundColor: statusConfig.bg, color: statusConfig.color, alignSelf: "center", marginBottom: 8 }]}>
                {statusConfig.label}
              </Text>
            )}
            <View style={{ flexDirection: "row", gap: 25, marginTop: 4 }}>
              <Text style={{ color: "#6b7280" }}># {invoice.invoiceNumber}</Text>
              <Text style={{ color: "#6b7280" }}>Date: {invoice.issueDate}</Text>
              <Text style={{ color: "#6b7280" }}>Due: {invoice.dueDate}</Text>
            </View>
          </View>
          <View style={styles.modernParties}>
            <View style={styles.modernPartyBox}>
              <Text style={styles.partyLabel}>From</Text>
              <Text style={[styles.partyText, { textAlign: "center" }]}>{invoice.from}</Text>
              {renderTaxIdLine(invoice.fromTaxId, "center")}
            </View>
            <View style={styles.modernPartyBox}>
              <Text style={styles.partyLabel}>Bill To</Text>
              <Text style={[styles.partyText, { textAlign: "center" }]}>{invoice.billTo}</Text>
              {renderTaxIdLine(invoice.billToTaxId, "center")}
            </View>
          </View>
          {renderTable()}
          {renderTotals()}
          {renderFooter()}
        </Page>
      </Document>
    )
  }

  if (invoice.layout === "split") {
    return (
      <Document>
        <Page size="A4" style={styles.page}>
          {renderWatermark()}
          <View style={styles.splitHeader}>
            {invoice.logo ? <PdfImage src={invoice.logo} style={styles.logo} /> : <View />}
            <View style={{ alignItems: "flex-end" }}>
              <Text style={[styles.title, { fontSize: 32, marginBottom: 0 }]}>{docTitle}</Text>
              {renderStatusBadge()}
            </View>
          </View>
          <View style={styles.splitMeta}>
            <View style={styles.splitMetaLeft}>
              <View style={styles.splitMetaRow}><Text style={[styles.splitMetaLabel, { width: 55 }]}>Number:</Text><Text style={{ fontWeight: "bold" }}>{invoice.invoiceNumber}</Text></View>
              <View style={styles.splitMetaRow}><Text style={[styles.splitMetaLabel, { width: 55 }]}>Date:</Text><Text>{invoice.issueDate}</Text></View>
              <View style={styles.splitMetaRow}><Text style={[styles.splitMetaLabel, { width: 55 }]}>Terms:</Text><Text>{invoice.paymentTerms}</Text></View>
              <View style={styles.splitMetaRow}><Text style={[styles.splitMetaLabel, { width: 55 }]}>Due:</Text><Text>{invoice.dueDate}</Text></View>
              {invoice.poNumber ? (
                <View style={styles.splitMetaRow}><Text style={[styles.splitMetaLabel, { width: 55 }]}>PO #:</Text><Text>{invoice.poNumber}</Text></View>
              ) : null}
            </View>
            <View style={styles.compactBalanceBox}>
              <Text style={styles.compactBalanceLabel}>
                {invoice.documentType === "receipt" ? "Amount Paid" : "Amount Due"}
              </Text>
              <Text style={styles.compactBalanceValue}>
                {formatMoney(invoice.documentType === "receipt" ? total : balanceDue)}
              </Text>
            </View>
          </View>
          <View style={[styles.parties, { gap: 20, marginBottom: 20 }]}>
            <View style={styles.splitPartyBox}>
              <Text style={styles.partyLabel}>From</Text>
              <Text style={styles.partyText}>{invoice.from}</Text>
              {renderTaxIdLine(invoice.fromTaxId)}
            </View>
            <View style={styles.splitPartyBox}>
              <Text style={styles.partyLabel}>Bill To</Text>
              <Text style={styles.partyText}>{invoice.billTo}</Text>
              {renderTaxIdLine(invoice.billToTaxId)}
            </View>
          </View>
          {renderTable()}
          {renderTotals()}
          {renderFooter()}
        </Page>
      </Document>
    )
  }

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {renderWatermark()}
        <View style={styles.header}>
          {invoice.logo ? <PdfImage src={invoice.logo} style={styles.logo} /> : <View />}
          <View>
            {renderStatusBadge()}
            <Text style={styles.title}>{docTitle}</Text>
            <View style={styles.metaRow}><Text style={styles.metaLabel}>#</Text><Text style={styles.metaValue}>{invoice.invoiceNumber}</Text></View>
            <View style={styles.metaRow}><Text style={styles.metaLabel}>Date</Text><Text style={styles.metaValue}>{invoice.issueDate}</Text></View>
            <View style={styles.metaRow}><Text style={styles.metaLabel}>Terms</Text><Text style={styles.metaValue}>{invoice.paymentTerms}</Text></View>
            <View style={styles.metaRow}><Text style={styles.metaLabel}>Due</Text><Text style={styles.metaValue}>{invoice.dueDate}</Text></View>
            {invoice.poNumber ? (
              <View style={styles.metaRow}><Text style={styles.metaLabel}>PO #</Text><Text style={styles.metaValue}>{invoice.poNumber}</Text></View>
            ) : null}
          </View>
        </View>
        <View style={styles.parties}>
          <View style={styles.partyCol}>
            <Text style={styles.partyLabel}>From</Text>
            <Text style={styles.partyText}>{invoice.from}</Text>
            {renderTaxIdLine(invoice.fromTaxId)}
          </View>
          <View style={styles.partyCol}>
            <Text style={styles.partyLabel}>Bill To</Text>
            <Text style={styles.partyText}>{invoice.billTo}</Text>
            {renderTaxIdLine(invoice.billToTaxId)}
          </View>
          {invoice.shipTo && (
            <View style={styles.partyCol}>
              <Text style={styles.partyLabel}>Ship To</Text>
              <Text style={styles.partyText}>{invoice.shipTo}</Text>
            </View>
          )}
        </View>
        {renderTable()}
        {renderTotals()}
        {renderFooter()}
      </Page>
    </Document>
  )
}
